import { useState, useCallback, useRef } from 'react';
import { userService } from '../api/services/user.service';

// Utility to build query params
const buildParams = (params) => Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'all'));

export function useUsers({ initialPageSize = 30 } = {}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const pageRef = useRef(1);
  const lastQueryRef = useRef(null);
  const hasMoreRef = useRef(false);
  const usersRef = useRef([]);

  // Keep refs in sync
  hasMoreRef.current = hasMore;
  usersRef.current = users;

  // Dedupe utility (by id first, fallback to phone unique key)
  const dedupeUsers = (arr) => {
    const seen = new Set();
    const out = [];
    for (const u of arr) {
      if (!u) continue;
      const key = u.id != null ? `id:${u.id}` : (u.phone_number || u["전화번호"]) ? `ph:${u.phone_number || u["전화번호"]}` : Math.random().toString(36);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(u);
    }
    return out;
  };

  const fetchUsers = useCallback(async (filters = {}, { reset = false } = {}) => {
    const query = buildParams({
      page: reset ? 1 : pageRef.current,
      page_size: initialPageSize,
      search: filters.search,
      purchase_within: filters.purchaseWithin,
      reservation_date: filters.reservationDate,
      friend_talk: filters.friendTalkEligibility, // 'eligible' | 'ineligible'
      channel_id: filters.channelId,
      sort: filters.sort,
      order: filters.order,
    });

    const queryKey = JSON.stringify({ ...query, page: undefined }); // exclude page from key
    if (reset) {
      pageRef.current = 1;
    }
    if (reset || lastQueryRef.current !== queryKey) {
      // New query baseline
      lastQueryRef.current = queryKey;
      setUsers([]);
      setHasMore(true);
    }

    if (!hasMore && !reset) return;

    setLoading(true);
    setError(null);
    try {
      const resp = await userService.getUsersList(query);
      const data = resp.data || [];
      const pageData = Array.isArray(data.results) ? data.results : data; // handle paginated or raw list
      setUsers(prev => {
        if (reset) {
          return dedupeUsers(pageData);
        } else {
          return dedupeUsers([...prev, ...pageData]);
        }
      });
      setTotalCount(data.count);
      if (pageData.length < initialPageSize) {
        setHasMore(false);
        console.log('No more users to load');
      } else {
        pageRef.current += 1;
      }
    } catch (e) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [initialPageSize, hasMore]);

  const resetAndFetch = useCallback((filters) => fetchUsers(filters, { reset: true }), [fetchUsers]);

  // Fetch all pages for current (or provided) filters
  const fetchAll = useCallback(async (filters = {}, { onProgress, maxPages = 200 } = {}) => {
    let allUsers = [];
    let page = 1;
    let pageSize = 500;
    while (page <= maxPages) {
      const query = buildParams({
        page,
        page_size: pageSize,
        search: filters.search,
        purchase_within: filters.purchaseWithin,
        reservation_date: filters.reservationDate,
        friend_talk: filters.friendTalkEligibility,
        channel_id: filters.channelId,
        sort: filters.sort,
        order: filters.order,
      });
      try {
        const resp = await userService.getUsersList(query);
        const data = resp.data || [];
        const pageData = Array.isArray(data.results) ? data.results : data;
        allUsers = dedupeUsers([...allUsers, ...pageData]);
        if (onProgress) onProgress([...allUsers], data.count); // report progress
        if (pageData.length < pageSize) {
          break; // last page reached
        }
        page += 1;
      } catch (e) {
        console.error('Error fetching users:', e);
        break;
      }
    }
    // Update state with all fetched users
    setUsers(dedupeUsers(allUsers));
    setHasMore(false);
    pageRef.current = page; // next page if needed
    lastQueryRef.current = JSON.stringify(buildParams({
      search: filters.search,
      purchase_within: filters.purchaseWithin,
      reservation_date: filters.reservationDate,
      friend_talk: filters.friendTalkEligibility,
      channel_id: filters.channelId,
      sort: filters.sort,
      order: filters.order,
    }));
    return allUsers;
  }, [resetAndFetch, fetchUsers]);
  
  const resetUsers = useCallback(() => {
    setUsers([]);
    setHasMore(false);
    setTotalCount(0);
    pageRef.current = 1;
    lastQueryRef.current = null;
  }, []);

  return {
    users,
    loading,
    error,
    hasMore,
    totalCount,
    fetchMore: fetchUsers,
    resetAndFetch,
    fetchAll,
    resetUsers
  };
}
